import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { NAV_LINKS, SECTION_IDS, scrollToSection } from '@/lib/index';

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (sectionId: string) => {
    scrollToSection(sectionId);
    setMobileOpen(false);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
      style={{
        background: scrolled
          ? 'rgba(10, 10, 20, 0.85)'
          : 'transparent',
        backdropFilter: scrolled ? 'blur(24px)' : 'none',
        borderBottom: scrolled ? '1px solid rgba(255,255,255,0.06)' : 'none',
      }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-18 py-4">
          {/* Logo */}
          <button
            onClick={() => scrollToSection(SECTION_IDS.HERO)}
            className="flex items-center gap-3 group"
            aria-label="KufutaPay+ — Início"
          >
            <div className="relative w-10 h-10">
              <img
                src="/images/logo_kufuta.jpeg"
                alt="KufutaPay+ Logo"
                className="w-full h-full object-contain rounded-lg"
                style={{
                  filter: 'brightness(1.1) contrast(1.1)',
                }}
              />
              <div
                className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{
                  boxShadow: '0 0 25px rgba(6, 182, 212, 0.6)',
                }}
              />
            </div>
            <span
              className="text-xl font-bold tracking-tight"
              style={{
                background: 'linear-gradient(90deg, #fff 0%, rgba(255,255,255,0.9) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              KufutaPay
              <span
                style={{
                  background: 'linear-gradient(90deg, #06B6D4, #67e8f9)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                +
              </span>
            </span>
          </button>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <button
                key={link.sectionId}
                onClick={() => handleNavClick(link.sectionId)}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white/60 hover:text-white transition-all duration-200 hover:bg-white/5 cursor-pointer"
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* Desktop CTAs */}
          <div className="hidden lg:flex items-center gap-3">
            <button
              onClick={() => scrollToSection(SECTION_IDS.CTA)}
              className="px-4 py-2 text-sm font-medium text-white/70 hover:text-white transition-colors duration-200 cursor-pointer"
            >
              Fale Connosco
            </button>
            <button
              onClick={() => scrollToSection(SECTION_IDS.CTA)}
              className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-all duration-300 cursor-pointer relative overflow-hidden group"
              style={{
                background: 'linear-gradient(135deg, #7C3AED 0%, #2563EB 100%)',
                boxShadow: '0 4px 15px rgba(124, 58, 237, 0.4)',
              }}
            >
              <span className="relative z-10">Começar Agora</span>
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{
                  background: 'linear-gradient(135deg, #6D28D9 0%, #1D4ED8 100%)',
                }}
              />
            </button>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="lg:hidden p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/5 transition-all"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Menu"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden"
            style={{
              background: 'rgba(10, 10, 20, 0.95)',
              backdropFilter: 'blur(24px)',
              borderTop: '1px solid rgba(255,255,255,0.06)',
            }}
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {NAV_LINKS.map((link) => (
                <button
                  key={link.sectionId}
                  onClick={() => handleNavClick(link.sectionId)}
                  className="px-4 py-3 rounded-lg text-sm font-medium text-white/70 hover:text-white hover:bg-white/5 transition-all text-left cursor-pointer"
                >
                  {link.label}
                </button>
              ))}
              <div className="pt-3 border-t border-white/5 mt-2">
                <button
                  onClick={() => handleNavClick(SECTION_IDS.CTA)}
                  className="w-full px-5 py-3 rounded-xl text-sm font-semibold text-white cursor-pointer"
                  style={{
                    background: 'linear-gradient(135deg, #7C3AED 0%, #2563EB 100%)',
                    boxShadow: '0 4px 15px rgba(124, 58, 237, 0.4)',
                  }}
                >
                  Começar Agora
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
